import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import SubscriptionPage from './pages/SubscriptionPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
import DashboardPage from './pages/DashboardPage';
import SearchOperatorsPage from './pages/SearchOperatorsPage';
import OperatorsPage from './pages/OperatorsPage';
import ReportsPage from './pages/ReportsPage';
import LicenseManagementPage from './pages/LicenseManagementPage';
import InventoryManagementPage from './pages/InventoryManagementPage';
import LabCoaManagementPage from './pages/LabCoaManagementPage';
import ComplianceReportsPage from './pages/ComplianceReportsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import SettingsPage from './pages/SettingsPage';
import ProductManagementPage from './pages/ProductManagementPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminOrganizationsPage from './pages/admin/AdminOrganizationsPage';
import AdminOperatorsPage from './pages/admin/AdminOperatorsPage';
import AdminPaymentsPage from './pages/admin/AdminPaymentsPage';
import AdminSubscriptionPlansPage from './pages/admin/AdminSubscriptionPlansPage';
import AdminAuditLogsPage from './pages/admin/AdminAuditLogsPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminProductsPage from './pages/admin/AdminProductsPage';
import AdminProductCategoriesPage from './pages/admin/AdminProductCategoriesPage';
import AdminTrialRequestsPage from './pages/admin/AdminTrialRequestsPage';
import { Toaster } from './components/ui/sonner';
import subscriptionService from './services/subscriptionService';
import authService from './services/authService';

function decodeJwtPayload(token) {
  try {
    // JWT uses base64url — convert to standard base64 before atob
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

function getRoleFromToken() {
  const token = localStorage.getItem('authToken');
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  return (
    payload['role'] ??
    payload['Role'] ??
    payload['roles'] ??
    payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ??
    null
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    checkAuthAndSubscription();
  }, []);

  const checkAuthAndSubscription = async () => {
    setIsCheckingAuth(true);
    
    // Check if user is authenticated
    const token = localStorage.getItem('authToken');
    const isAuth = !!token;
    setIsAuthenticated(isAuth);

    if (isAuth) {
      // Admin users bypass subscription check
      const roleName = authService.getCurrentUser()?.roleName || getRoleFromToken();
      if (roleName === 'Admin') {
        setIsCheckingAuth(false);
        return;
      }

      // User is logged in, check subscription status
      try {
        console.log('Checking subscription status for authenticated user...');
        const subscriptionStatus = await subscriptionService.checkSubscriptionStatus();
        console.log('Subscription status:', subscriptionStatus);

        // If subscription is not active and user is not already on subscription/payment pages
        if (subscriptionStatus.status !== 'ACTIVE') {
          const currentPath = window.location.pathname;
          console.log('Subscription not active. Current path:', currentPath);

          // Only redirect if not already on subscription/payment pages
          if (!currentPath.includes('/subscription') &&
              !currentPath.includes('/payment-success') &&
              !currentPath.includes('/payment-cancel') &&
              !currentPath.includes('/login') &&
              !currentPath.includes('/register') &&
              !currentPath.includes('/forgot-password') &&
              currentPath !== '/') {

            console.log('Redirecting to subscription page...');
            // Redirect to subscription page
            window.location.href = '/subscription';
          }
        } else {
          console.log('Subscription is ACTIVE, allowing access');
        }
      } catch (error) {
        console.error('Failed to check subscription status:', error);
      }
    }
    
    setIsCheckingAuth(false);
  };

  const ProtectedRoute = ({ children }) => {
    const [hasAccess, setHasAccess] = useState(null);

    useEffect(() => {
      const checkAccess = async () => {
        console.log('ProtectedRoute: Checking access...', 'isAuthenticated:', isAuthenticated);

        if (!isAuthenticated) {
          console.log('ProtectedRoute: Not authenticated, denying access');
          setHasAccess(false);
          return;
        }

        // Admin users always have access
        const adminRole = authService.getCurrentUser()?.roleName || getRoleFromToken();
        if (adminRole === 'Admin') {
          setHasAccess(true);
          return;
        }

        try {
          console.log('ProtectedRoute: Checking subscription status...');
          
          // Set a safety timeout to prevent hanging
          const timeoutId = setTimeout(() => {
            console.warn('ProtectedRoute: Subscription check taking too long, denying access');
            setHasAccess(false);
          }, 12000); // 12 second safety net
          
          const subscriptionStatus = await subscriptionService.checkSubscriptionStatus();
          
          // Clear the timeout since we got a response
          clearTimeout(timeoutId);
          
          console.log('ProtectedRoute: Subscription status:', subscriptionStatus);
          
          if (subscriptionStatus.status === 'ACTIVE') {
            console.log('ProtectedRoute: Access granted (ACTIVE subscription)');
            setHasAccess(true);
          } else {
            console.log('ProtectedRoute: Access denied (subscription not ACTIVE)');
            setHasAccess(false);
          }
        } catch (error) {
          console.error('ProtectedRoute: Subscription check failed:', error);
          setHasAccess(false);
        }
      };

      checkAccess();
    }, []);

    if (hasAccess === null) {
      // Still checking
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Checking subscription...</p>
          </div>
        </div>
      );
    }

    if (!isAuthenticated) {
      return <Navigate to="/login" />;
    }

    if (!hasAccess) {
      return <Navigate to="/subscription" />;
    }

    return children;
  };

  const AdminRoute = ({ children }) => {
    if (!isAuthenticated) return <Navigate to="/login" />;
    const role = authService.getCurrentUser()?.roleName || getRoleFromToken();
    if (role !== 'Admin') return <Navigate to="/dashboard" />;
    return children;
  };

  // Show loading while checking initial auth
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage setIsAuthenticated={setIsAuthenticated} />} />
          <Route path="/register" element={<RegisterPage setIsAuthenticated={setIsAuthenticated} />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/payment-cancel" element={<PaymentCancelPage />} />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/search-operators" element={
            <ProtectedRoute>
              <SearchOperatorsPage />
            </ProtectedRoute>
          } />
          <Route path="/operators" element={
            <ProtectedRoute>
              <OperatorsPage />
            </ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute>
              <ReportsPage />
            </ProtectedRoute>
          } />
          <Route path="/licenses" element={
            <ProtectedRoute>
              <LicenseManagementPage />
            </ProtectedRoute>
          } />
          <Route path="/inventory" element={
            <ProtectedRoute>
              <InventoryManagementPage />
            </ProtectedRoute>
          } />
          <Route path="/lab-coa" element={
            <ProtectedRoute>
              <LabCoaManagementPage />
            </ProtectedRoute>
          } />
          <Route path="/compliance-reports" element={
            <ProtectedRoute>
              <ComplianceReportsPage />
            </ProtectedRoute>
          } />
          <Route path="/analytics" element={
            <ProtectedRoute>
              <AnalyticsPage />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          } />
          <Route path="/products" element={
            <ProtectedRoute>
              <ProductManagementPage />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={<Navigate to="/admin/dashboard" />} />
          <Route path="/admin/dashboard" element={
            <AdminRoute>
              <AdminDashboardPage />
            </AdminRoute>
          } />
          <Route path="/admin/users" element={
            <AdminRoute>
              <AdminUsersPage />
            </AdminRoute>
          } />
          <Route path="/admin/organizations" element={
            <AdminRoute>
              <AdminOrganizationsPage />
            </AdminRoute>
          } />
          <Route path="/admin/operators" element={
            <AdminRoute>
              <AdminOperatorsPage />
            </AdminRoute>
          } />
          <Route path="/admin/payments" element={
            <AdminRoute>
              <AdminPaymentsPage />
            </AdminRoute>
          } />
          <Route path="/admin/subscription-plans" element={
            <AdminRoute>
              <AdminSubscriptionPlansPage />
            </AdminRoute>
          } />
          <Route path="/admin/audit-logs" element={
            <AdminRoute>
              <AdminAuditLogsPage />
            </AdminRoute>
          } />
          <Route path="/admin/products" element={
            <AdminRoute>
              <AdminProductsPage />
            </AdminRoute>
          } />
          <Route path="/admin/product-categories" element={
            <AdminRoute>
              <AdminProductCategoriesPage />
            </AdminRoute>
          } />
          <Route path="/admin/trial-requests" element={
            <AdminRoute>
              <AdminTrialRequestsPage />
            </AdminRoute>
          } />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;